/**
 * SuggestInput — a text box that offers matching values from the server as you type.
 *
 * Jesse, 2026-10-30, on the Audit Log's IP address box: a select list would be
 * fine with a handful of addresses but unusable with thousands. This works at both
 * sizes: on focus it offers the most recently seen values, typing narrows them, and
 * the server only ever sends a short list (the endpoint's limit), so the page never
 * holds the whole set. It stays a free-text box, so a value nobody has suggested
 * can still be typed.
 *
 * Built on the native <datalist>: keyboard, screen-reader and touch behaviour come
 * from the browser rather than from a hand-rolled listbox.
 */
import { useEffect, useId, useState } from 'react'
import { useDebouncedValue } from '../hooks/useDebouncedValue'

export interface Suggestion {
  value: string
  /** Shown beside the value, e.g. "12 entries · last 10/30". */
  hint?: string
}

interface SuggestInputProps {
  id: string
  value: string
  onChange: (value: string) => void
  fetchSuggestions: (query: string) => Promise<Suggestion[]>
  placeholder?: string
  maxLength?: number
  className?: string
}

export default function SuggestInput({
  id, value, onChange, fetchSuggestions, placeholder, maxLength, className = 'input text-sm',
}: SuggestInputProps) {
  const listId = `${useId()}-suggestions`
  const [active, setActive] = useState(false)
  const [options, setOptions] = useState<Suggestion[]>([])
  const query = useDebouncedValue(value)

  // Only while the box has focus: nothing is fetched for a page that merely shows it.
  useEffect(() => {
    if (!active) return
    let cancelled = false
    fetchSuggestions(query.trim())
      .then((found) => { if (!cancelled) setOptions(found) })
      .catch(() => { if (!cancelled) setOptions([]) })
    return () => { cancelled = true }
  }, [active, query, fetchSuggestions])

  return (
    <>
      <input
        id={id}
        className={className}
        list={listId}
        autoComplete="off"
        placeholder={placeholder}
        maxLength={maxLength}
        value={value}
        onFocus={() => setActive(true)}
        onBlur={() => setActive(false)}
        onChange={(e) => onChange(e.target.value)}
      />
      <datalist id={listId}>
        {options.map((o) => (
          <option key={o.value} value={o.value} label={o.hint} />
        ))}
      </datalist>
    </>
  )
}
